import type { ReactNode } from 'react';
import { memo, use, useMemo, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import { Box, CircularProgress, Collapse, Divider, Stack } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { useControllableState } from '@trinity/chat-components/hooks/useControllableState';
import { createNamedContext } from '@trinity/chat-components/utils';
import type { CardPhase } from './interfaces';

interface ChainOfThoughtContextValue {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

const ChainOfThoughtContext = createNamedContext<ChainOfThoughtContextValue | undefined>(
  undefined,
  'ChainOfThoughtContext'
);

const useChainOfThoughtContext = () => {
  const ctx = use(ChainOfThoughtContext);

  if (ctx === undefined) {
    throw new Error('ChainOfThought components must be used within a ChainOfThought.Root');
  }

  return ctx;
};

interface ChainOfThoughtRootProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: ReactNode;
  className?: string;
}

const ChainOfThoughtRoot = memo(
  ({ open, onOpenChange, defaultOpen = false, children, className }: ChainOfThoughtRootProps) => {
    const [isOpen, setIsOpen] = useControllableState({
      defaultProp: defaultOpen,
      onChange: onOpenChange,
      prop: open,
    });

    const ctx = useMemo(() => ({ isOpen, setIsOpen }), [isOpen, setIsOpen]);

    return (
      <ChainOfThoughtContext.Provider value={ctx}>
        <Stack spacing={2} className={className}>
          {children}
        </Stack>
      </ChainOfThoughtContext.Provider>
    );
  }
);
ChainOfThoughtRoot.displayName = 'ChainOfThought.Root';

interface ChainOfThoughtHeaderProps {
  phase: CardPhase;
  subtitle?: string;
  completedSteps?: number;
  totalSteps?: number;
  children?: ReactNode;
}

const ICON_SIZE = 18;
const STROKE_WIDTH = 2;
const RADIUS = (ICON_SIZE - STROKE_WIDTH) / 2;
const CENTER = ICON_SIZE / 2;
const SEGMENT_GAP_DEG = 6; // degrees of gap between segments

function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number): { x: number; y: number } {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function describeArc(cx: number, cy: number, r: number, startDeg: number, endDeg: number): string {
  const start = polarToCartesian(cx, cy, r, endDeg);
  const end = polarToCartesian(cx, cy, r, startDeg);
  const largeArc = endDeg - startDeg > 180 ? 1 : 0;
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 0 ${end.x} ${end.y}`;
}

interface SegmentedProgressIconProps {
  completedSteps: number;
  totalSteps: number;
}

const SegmentedProgressIcon = memo(({ completedSteps, totalSteps }: SegmentedProgressIconProps) => {
  const segments = useMemo(() => {
    const count = Math.max(totalSteps, 1);
    const gapTotal = count * SEGMENT_GAP_DEG;
    const available = 360 - gapTotal;
    const segDeg = available / count;
    const result: Array<{ d: string; filled: boolean }> = [];

    for (let i = 0; i < count; i++) {
      const startDeg = i * (segDeg + SEGMENT_GAP_DEG) + SEGMENT_GAP_DEG / 2;
      const endDeg = startDeg + segDeg;
      result.push({ d: describeArc(CENTER, CENTER, RADIUS, startDeg, endDeg), filled: i < completedSteps });
    }
    return result;
  }, [completedSteps, totalSteps]);

  return (
    <svg width={ICON_SIZE} height={ICON_SIZE} viewBox={`0 0 ${ICON_SIZE} ${ICON_SIZE}`} fill="none">
      {/* Segmented circle border */}
      {segments.map((seg, i) => (
        <path
          key={i}
          d={seg.d}
          stroke={seg.filled ? 'currentColor' : 'rgba(128,128,128,0.3)'}
          strokeWidth={STROKE_WIDTH}
          strokeLinecap="round"
          fill="none"
        />
      ))}
      {/* Lightning bolt */}
      <path d="M10.5 4L7 10h2.5L8 14l4.5-6H10l.5-4z" fill="currentColor" />
    </svg>
  );
});
SegmentedProgressIcon.displayName = 'SegmentedProgressIcon';

function HeaderIcon({
  phase,
  completedSteps = 0,
  totalSteps = 0,
}: {
  phase: CardPhase;
  completedSteps?: number;
  totalSteps?: number;
}) {
  switch (phase) {
    case 'thinking':
      return <CircularProgress size={26} thickness={5} sx={{ color: 'text.secondary' }} />;
    case 'executing':
      return <SegmentedProgressIcon completedSteps={completedSteps} totalSteps={totalSteps} />;
    case 'complete':
      return (
        <svg width={26} height={26} viewBox="0 0 24 24" fill="none">
          <circle cx={12} cy={12} r={10} stroke="currentColor" strokeWidth={2} fill="none" />
          <path
            d="M8 12.5l2.5 2.5L16 9.5"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </svg>
      );
    case 'cancelled':
      return <PauseCircleOutlineIcon sx={{ fontSize: 26 }} />;
  }
}

const ChainOfThoughtHeader = memo(
  ({ phase, subtitle, completedSteps, totalSteps, children }: ChainOfThoughtHeaderProps) => {
    const { isOpen, setIsOpen } = useChainOfThoughtContext();

    return (
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        onClick={() => setIsOpen(!isOpen)}
        sx={{
          cursor: 'pointer',
          color: 'text.secondary',
        }}
      >
        <HeaderIcon phase={phase} completedSteps={completedSteps} totalSteps={totalSteps} />
        <Stack sx={{ flex: 1, textAlign: 'left' }}>
          <HeaderCopy>{children ?? 'Chain of Thought'}</HeaderCopy>
          {subtitle ? <SubtitleCopy>{subtitle}</SubtitleCopy> : null}
        </Stack>
        <ExpandMoreIcon
          sx={{
            fontSize: 18,
            transition: 'transform 200ms',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </Stack>
    );
  }
);
ChainOfThoughtHeader.displayName = 'ChainOfThought.Header';

interface ChainOfThoughtContentProps {
  children: ReactNode;
}

const ChainOfThoughtContent = memo(({ children }: ChainOfThoughtContentProps) => {
  const { isOpen } = useChainOfThoughtContext();

  return (
    <Collapse in={isOpen} timeout="auto" unmountOnExit>
      <Stack spacing={0.75} sx={{ mt: 1 }}>
        {children}
      </Stack>
    </Collapse>
  );
});
ChainOfThoughtContent.displayName = 'ChainOfThought.Content';

const fadeSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const shimmerPulse = keyframes`
  0%   { opacity: 1; }
  50%  { opacity: 0.4; }
  100% { opacity: 1; }
`;

const StepConnector = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 28,
  bottom: 0,
  left: '50%',
  width: 1,
  marginLeft: -0.5,
  backgroundColor: theme.palette.divider,
}));

const statusColor = {
  complete: 'text.secondary',
  active: 'text.primary',
  pending: 'text.disabled',
} as const;

const HeaderCopy = styled('div')(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: '16px',
  fontFamily: theme.fonts.semiBold,
}));

const SubtitleCopy = styled('div')(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '12px',
  fontFamily: theme.fonts.semiBold,
}));

const CaptionText = styled('span')(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

interface ChainOfThoughtStepProps {
  icon?: ReactNode;
  label: ReactNode;
  description?: ReactNode;
  status?: 'complete' | 'active' | 'pending';
  isLast?: boolean;
  children?: ReactNode;
}

const ChainOfThoughtStep = memo(
  ({ icon, label, description, status = 'complete', isLast = false, children }: ChainOfThoughtStepProps) => {
    const isActive = status === 'active';

    return (
      <Stack
        direction="row"
        spacing={1}
        sx={{
          color: statusColor[status],
          animation: `${fadeSlideIn} 300ms ease-out`,
          fontSize: '0.875rem',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            mt: '2px',
            flexShrink: 0,
            width: 26,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          {icon ?? <FiberManualRecordIcon sx={{ fontSize: 18 }} />}
          {!isLast && <StepConnector />}
        </Box>
        <Stack spacing={0.5} sx={{ flex: 1, overflow: 'hidden', minWidth: 0, pb: isLast ? 0 : 0.5 }}>
          <Box sx={isActive ? { animation: `${shimmerPulse} 1.4s ease-in-out infinite` } : undefined}>
            {label}
          </Box>
          {description ? <CaptionText>{description}</CaptionText> : null}
          {children}
        </Stack>
      </Stack>
    );
  }
);
ChainOfThoughtStep.displayName = 'ChainOfThought.Step';

interface ChainOfThoughtStepResultProps {
  children: ReactNode;
}

const ChainOfThoughtStepResult = memo(({ children }: ChainOfThoughtStepResultProps) => (
  <CaptionText style={{ fontStyle: 'italic' }}>{children}</CaptionText>
));
ChainOfThoughtStepResult.displayName = 'ChainOfThought.StepResult';

const ParallelGroupConnector = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: 0,
  width: 2,
  borderRadius: 1,
  backgroundColor: theme.palette.primary.main,
  opacity: 0.4,
}));

interface ChainOfThoughtParallelGroupProps {
  children: ReactNode;
}

const ChainOfThoughtParallelGroup = memo(({ children }: ChainOfThoughtParallelGroupProps) => (
  <Stack spacing={0.75}>
    <CaptionText>Run at the same time</CaptionText>
    <Box sx={{ position: 'relative', pl: 1.5 }}>
      <ParallelGroupConnector />
      <Stack spacing={0.75}>{children}</Stack>
    </Box>
  </Stack>
));
ChainOfThoughtParallelGroup.displayName = 'ChainOfThought.ParallelGroup';

const ChainOfThoughtCancellationDivider = memo(() => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 0.5 }}>
    <Divider sx={{ flex: 1 }} />
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <PauseCircleOutlineIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
      <span style={{ fontSize: '0.75rem', color: 'inherit', opacity: 0.5 }}>Paused by user</span>
    </Stack>
    <Divider sx={{ flex: 1 }} />
  </Stack>
));
ChainOfThoughtCancellationDivider.displayName = 'ChainOfThought.CancellationDivider';

const PREVIEW_LENGTH = 80;

interface ChainOfThoughtCollapsibleDescriptionProps {
  text: string;
  fallback?: ReactNode;
}

const ChainOfThoughtCollapsibleDescription = memo(
  ({ text, fallback }: ChainOfThoughtCollapsibleDescriptionProps) => {
    const [expanded, setExpanded] = useState(false);
    const isLong = text.length > PREVIEW_LENGTH;
    const preview = isLong ? `${text.slice(0, PREVIEW_LENGTH)}...` : text;

    return (
      <Stack spacing={0.25}>
        <Collapse in={expanded} collapsedSize={0}>
          <CaptionText style={{ whiteSpace: 'pre-wrap' }}>{text}</CaptionText>
        </Collapse>
        {!expanded && <CaptionText>{preview}</CaptionText>}
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          {isLong && (
            <CaptionText
              as="button"
              onClick={() => setExpanded((prev) => !prev)}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                textDecoration: 'underline',
                color: 'inherit',
                fontSize: 'inherit',
                fontFamily: 'inherit',
              }}
            >
              {expanded ? 'Show less' : 'Show more'}
            </CaptionText>
          )}
          {fallback && !expanded ? <CaptionText>{fallback}</CaptionText> : null}
        </Box>
      </Stack>
    );
  }
);
ChainOfThoughtCollapsibleDescription.displayName = 'ChainOfThought.CollapsibleDescription';

export const ChainOfThought = {
  Root: ChainOfThoughtRoot,
  Header: ChainOfThoughtHeader,
  Content: ChainOfThoughtContent,
  Step: ChainOfThoughtStep,
  StepResult: ChainOfThoughtStepResult,
  ParallelGroup: ChainOfThoughtParallelGroup,
  CancellationDivider: ChainOfThoughtCancellationDivider,
  CollapsibleDescription: ChainOfThoughtCollapsibleDescription,
};
